import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Bus, ExternalLink, MapPin, X } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { RED_API, fmtDate, useMeta } from "../components/red/api";
import { Select, Spinner } from "../components/red/ui";

const mediaUrl = (g, m) => `${RED_API}/public/gira/${g.id}/media/${m.file_id}`;
const TIPO = { foto: "Foto", video: "Video", entrevista: "Entrevista" };

const Visita = ({ g, onOpen, i }) => (
  <li className="relative pl-10 sm:pl-14" data-testid={`gira-pub-item-${g.id}`} style={{ animation: `fadeUp .5s ease ${Math.min(i, 8) * 70}ms both` }}>
    <span className="absolute left-0 top-1 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#F5B400] text-[#0A192F] flex items-center justify-center shadow"><Bus size={16} /></span>
    <div className="font-headings uppercase tracking-[0.2em] text-xs text-[#D97706]">{fmtDate(g.fecha)}</div>
    <h2 className="font-headings uppercase font-bold text-2xl sm:text-3xl text-[#0A192F] mt-1 flex items-center gap-2"><MapPin size={20} className="text-[#0A2463]" />{g.ciudad}, {g.provincia}</h2>
    <p className="mt-3 text-[#374151] leading-relaxed whitespace-pre-line max-w-3xl">{g.descripcion}</p>
    {g.media?.length > 0 && (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-5">
        {g.media.map((m) => (
          <button key={m.file_id} onClick={() => onOpen(g, m)} className="relative aspect-video bg-[#0A192F] overflow-hidden group" data-testid={`gira-pub-media-${m.file_id}`}>
            {m.tipo === "foto" ? (
              <img src={mediaUrl(g, m)} alt={m.titulo} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            ) : (
              <video src={mediaUrl(g, m)} preload="metadata" muted playsInline className="w-full h-full object-cover" />
            )}
            <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white text-[11px] text-left px-2 pt-6 pb-1.5 truncate">{TIPO[m.tipo]} · {m.titulo}</span>
          </button>
        ))}
      </div>
    )}
    {g.enlaces?.length > 0 && (
      <div className="flex flex-wrap gap-3 mt-4">
        {g.enlaces.map((l) => (
          <a key={l} href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#0A2463] border border-[#0A2463]/30 hover:border-[#0A2463] px-3 py-1.5 transition-colors">
            <ExternalLink size={13} /> Ver publicación
          </a>
        ))}
      </div>
    )}
  </li>
);

export default function GiraPublica() {
  const [activa, setActiva] = useState(null);
  const [items, setItems] = useState([]);
  const [prov, setProv] = useState("");
  const [open, setOpen] = useState(null);
  const [meta] = useMeta();

  useEffect(() => { axios.get(`${RED_API}/public/estado`).then((r) => setActiva(r.data.gira)).catch(() => setActiva(false)); }, []);
  useEffect(() => {
    if (activa) axios.get(`${RED_API}/public/gira`, { params: { provincia: prov } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [activa, prov]);

  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid="gira-publica">
      <style>{"@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}"}</style>
      <header className="bg-[#0A192F] text-white relative overflow-hidden">
        <div aria-hidden className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#F5B400]/15 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 py-10">
          <div className="flex items-center justify-between gap-4">
            <Link to="/"><LogoMark className="h-9" variant="light" /></Link>
            <Link to="/" className="text-sm text-white/70 hover:text-[#F5B400] font-headings uppercase tracking-wide" data-testid="gira-pub-volver">← Volver al sitio</Link>
          </div>
          <div className="mt-8 font-headings uppercase tracking-[0.3em] text-xs text-[#F5B400]">Recorrido por el país</div>
          <h1 className="font-headings uppercase font-bold text-4xl sm:text-5xl lg:text-6xl mt-2">Gira de la Esperanza</h1>
          <p className="text-white/70 mt-3 max-w-xl">Cada parada del ómnibus de Frente Ya: lo que hicimos, con quiénes hablamos y lo que nos contaron.</p>
          {activa && <p className="mt-5 text-sm text-white/80" data-testid="gira-pub-contador"><strong className="text-[#F5B400] text-2xl font-headings mr-1">{items.length}</strong> paradas registradas</p>}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-10">
        {activa === null ? <Spinner /> : !activa ? (
          <div className="bg-white border p-10 text-center text-gray-500" data-testid="gira-no-disponible">La Gira de la Esperanza todavía no está disponible.</div>
        ) : (
          <>
            <div className="max-w-xs mb-10">
              <Select value={prov} placeholder="Todas las provincias" options={meta.provincias} onChange={(e) => setProv(e.target.value)} data-testid="gira-pub-provincia" />
            </div>
            {items.length === 0 ? <div className="bg-white border p-10 text-center text-gray-500">No hay paradas publicadas todavía.</div> : (
              <ol className="relative space-y-14 before:absolute before:left-4 sm:before:left-5 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#F5B400]/50">
                {items.map((g, i) => <Visita key={g.id} g={g} i={i} onOpen={(gg, m) => setOpen({ g: gg, m })} />)}
              </ol>
            )}
          </>
        )}
      </main>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-3" onClick={() => setOpen(null)} data-testid="gira-pub-modal">
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center text-white mb-2"><span className="text-sm">{TIPO[open.m.tipo]} · {open.m.titulo} — {open.g.ciudad}</span><button onClick={() => setOpen(null)} data-testid="gira-pub-modal-close"><X /></button></div>
            {open.m.tipo === "foto" ? <img src={mediaUrl(open.g, open.m)} alt={open.m.titulo} className="w-full max-h-[80vh] object-contain" /> : <video src={mediaUrl(open.g, open.m)} controls autoPlay playsInline className="w-full max-h-[80vh] bg-black" />}
          </div>
        </div>
      )}
    </div>
  );
}
